import Suspense from '@/Components/Suspense';
import { type DropDownMenuItemProps, DropdownMenu } from '@/Components/filterbar/components/DropdownMenu';
import { useLatestValuesRef } from '@/Utility/hooks/useLatestValuesRef';
import {
  HubsPaginationFragment,
  HubsQuery,
  type Hubs_Pagination_Fragment$key,
  type Hubs_Query,
} from '@/graphql/queries/Hub.query';
import { usePaginationFragment } from '@/graphql/relay.utils';
import { atom, useAtom } from 'jotai';
import React, { useEffect, useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { z } from 'zod';
import { useFilterBarCtx } from '../FilterBar';
import { FilterBadge } from '../components/FilterBadge';
import { __useFilterState } from '../useFilterState';

export const hubFilterSearchSchema = z.object({
  hubFilter: z
    .object({
      hubIds: z.array(z.string()),
      pageIds: z.array(z.string()),
    })
    .nullish(),
});

export const hubFilterComponentProps = z.object({
  displayName: z.string().optional(),
});

type HubFilterProps = z.infer<typeof hubFilterComponentProps>;

const HubsArray = atom<Array<z.infer<typeof hubFilterSearchSchema>['hubFilter'] & { title: string }>>([]);

const HubFilterInner = ({ displayName = 'Hubs' }: HubFilterProps) => {
  const { props: ctxProps } = useFilterBarCtx();

  const queryRef = useLazyLoadQuery<Hubs_Query>(HubsQuery, {
    first: 24,
    where: {
      search: '',
      searchFields: ['Title', 'Description', 'Group'],
      statusIn: ['PUBLISH', 'HIDDEN', 'HIDDEN_B_S'],
    },
  });

  const { data, refetch, ...relayProps } = usePaginationFragment<Hubs_Query, Hubs_Pagination_Fragment$key>(
    HubsPaginationFragment,
    queryRef,
    { usePageLoading: false }
  );

  const { slowFilters, fastFilters, updateFilter, isUpdating } = __useFilterState(hubFilterSearchSchema);

  const relayHubs = useMemo(() => {
    if (!data?.hubs?.edges) return [];
    return data.hubs.edges.map(({ node }) => node);
  }, [data?.hubs?.edges]);

  const [hubsArray, setHubsArray] = useAtom(HubsArray);

  useEffect(() => {
    if (!data?.hubs?.edges?.length) return;

    setHubsArray((prev) => {
      relayHubs.forEach((item) => {
        const found = prev.find((hub) => hub?.hubIds?.includes?.(item.id));
        if (!found) {
          prev.push({
            title: item.title,
            hubIds: [item.id],
            pageIds:
              item.connectionHubToHps?.edges
                .map((edge) => edge.node.hubPage?.node?.id)
                .filter((id): id is string => Boolean(id)) || [],
          });
        }
      });

      return [...prev];
    });
  }, [relayHubs]);

  const menuItems: DropDownMenuItemProps[] = useMemo(() => {
    if (!hubsArray?.length) return [];

    const items = [] as DropDownMenuItemProps[];

    hubsArray.forEach((item) => {
      const hubId = item?.hubIds?.[0];

      items.push({
        name: item.title,
        value: {
          hubIds: [hubId],
          pageIds: item.pageIds,
        },
        onClick: () => {
          updateFilter((prev) => {
            let newHubIds = [...(prev?.hubFilter?.hubIds || []), hubId];
            if (prev?.hubFilter?.hubIds?.includes(hubId)) {
              newHubIds = prev.hubFilter.hubIds.filter((id) => id !== hubId);
            }

            const newPageIds = Array.from(
              new Set(
                hubsArray.flatMap((item) => {
                  if (newHubIds.includes(item.hubIds[0])) {
                    return item.pageIds;
                  }
                  return [];
                })
              )
            );

            if (newHubIds.length === 0) {
              return {
                hubFilter: undefined,
              };
            }

            return {
              hubFilter: {
                hubIds: newHubIds,
                pageIds: newPageIds,
              },
            };
          });
        },
      });
    });

    return items;
  }, [hubsArray, updateFilter]);

  const loadingProps = useLatestValuesRef({
    hasNext: relayProps?.hasNext,
    isLoadingNext: relayProps?.isLoadingNext,
    isRefetching: relayProps?.isRefetching,
    loadNext: relayProps?.loadNext,
  });

  return (
    <div style={{ position: 'relative' }}>
      <DropdownMenu
        className={ctxProps?.variant === 'default' ? 'z-btn-6' : 'z-btn-compact'}
        name={displayName || 'Hubs'}
        menuList={menuItems}
        type="multiple-select"
        options={{
          filterBar: slowFilters,
          filterBarKey: 'hubFilter',
          filterBarComparison(itemState, filterParam) {
            if (!filterParam) return false;
            if (!itemState) return false;

            type Filter = z.infer<typeof hubFilterSearchSchema>['hubFilter'];
            const item = filterParam as Filter;
            const comparison = (itemState as Filter)?.hubIds?.some((id) => item?.hubIds?.includes?.(id));
            return comparison;
          },

          isSearchable: true,
          showCheckedOnTop: true,
          clearSearchOnClose: true,
          isLoading: relayProps?.isLoadingNext || relayProps?.isRefetching,
          onScrollEnd: () => {
            const loading = loadingProps.current;
            if (loading?.hasNext && !loading?.isLoadingNext && !loading?.isRefetching) {
              loading?.loadNext?.(10);
            }
          },
          onSearchChange(search) {
            refetch({
              first: 24,
              where: {
                statusIn: ['PUBLISH', 'HIDDEN', 'HIDDEN_B_S'],
                search,
                searchFields: ['Title', 'Description', 'Group'],
              },
            });
          },
        }}
      />
      <FilterBadge count={fastFilters?.hubFilter?.hubIds.length || 0} isUpdating={isUpdating} />
    </div>
  );
};

const HubFilter = (props: HubFilterProps) => {
  return (
    <Suspense
      fallback={
        <DropdownMenu
          className="z-btn-6"
          name={props.displayName || 'Hubs'}
          menuList={[]}
          type="multiple-select"
          options={{
            isSearchable: true,
          }}
        />
      }
    >
      <HubFilterInner {...props} />
    </Suspense>
  );
};
const HubFilterMemo = React.memo(HubFilter);

export const FilterConfig_Hub = {
  hubFilter: {
    searchSchema: hubFilterSearchSchema,
    component: HubFilterMemo,
    componentProps: hubFilterComponentProps,
  },
} as const;
